import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./userSettings.module.scss";
import { UsersSide } from "./users";
import { UserInfo } from "./info";
import { getAllRolesData, getEmployeesData } from "./services";
import { useAppDispatch } from "../../redux";
import { activeValues, ActiveValuesKeysType } from "./users/filters";
import { Employee } from "../../types";
import { PaginationType } from "../ManageAccess/types";

const LIMIT = 30;
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 400;

export type FiltersType = {
  search?: string;
  is_active?: ActiveValuesKeysType;
  group_id: string | number;
  position_id: string | number;
};
export const UserSettings = () => {
  const dispatch = useAppDispatch();
  const [activeUser, setActiveUser] = useState<Employee | null>(null);
  const [data, setData] = useState<Employee[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<FiltersType>({
    search: "",
    is_active: activeValues.active,
    group_id: "",
    position_id: "",
  });

  useEffect(() => {
    dispatch(getAllRolesData());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = filters.search || "";
        if (prev !== next) setPage(1);
        return next;
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const getEmployees = async () => {
      const params: Record<string, any> = {
        group_id: filters.group_id,
        position_id: filters.position_id,
        page,
        limit: LIMIT,
      };
      if (filters.is_active !== activeValues.all) {
        params.is_active = filters.is_active === "active" ? 1 : 0;
      }
      if (debouncedSearch.length >= MIN_SEARCH_LENGTH) {
        params.search = debouncedSearch;
      }
      const res = await getEmployeesData(params, setIsFetching);
      setData(res.employees);
      setPagination(res.pagination);
    };
    getEmployees();
  }, [filters.group_id, filters.position_id, filters.is_active, debouncedSearch, page]);

  const changeActiveUser = (user) => setActiveUser(user);

  const changeCities = (employee_id: number, city_name: string) => {
    setData((prev) =>
      prev?.map((item) => {
        if (item?.employee_id === employee_id) {
          return { ...item, city_name };
        }
        return item;
      })
    );
  };

  const handleFiltersChange = useCallback((name: string, value: any) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name !== "search") {
      setPage(1);
    }
  }, []);

  const handlePageChange = useCallback((_: any, newPage: number) => {
    setPage(newPage);
  }, []);

  const filtersAsParams = useMemo(() => {
    const params: Record<string, any> = {
      group_id: filters.group_id,
      position_id: filters.position_id,
      page,
      limit: LIMIT,
    };
    if(filters.is_active !== activeValues.all) {
      params.is_active = filters.is_active === 'active' ? 1 : 0;
    }
    if (debouncedSearch.length >= MIN_SEARCH_LENGTH) {
      params.search = debouncedSearch;
    }
    return params;
  }, [filters.group_id, filters.position_id, filters.is_active, debouncedSearch, page]);

  return (
    <div className={styles.settings__wrapper}>
      <div className={styles.settings__users}>
        <UsersSide
          data={data}
          filtersAsParams={filtersAsParams}
          filters={filters}
          handleFiltersChange={handleFiltersChange}
          setData={setData}
          isFetching={isFetching}
          changeActiveUser={changeActiveUser}
          activeUserId={activeUser?.employee_id}
          page={page}
          totalPages={pagination?.total_pages || 0}
          totalCount={pagination?.total_count || 0}
          onPageChange={handlePageChange}
        />
      </div>
      <div className={styles.settings__info}>
        <UserInfo activeUser={activeUser} changeCities={changeCities} />
      </div>
    </div>
  );
};
